import { AnimatePresence, motion } from "motion/react";
import { CornerDownLeft, Search } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import { useOverlay } from "../ui/components/Overlay";
import { Kbd } from "../ui/components/Kbd";
import { score, useCommands, type Command } from "./commands";

export const isMac = (): boolean =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** ⌘K / Ctrl+K: jump anywhere, run actions (plan "Keyboard-first"). */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.altKey &&
        e.key.toLowerCase() === "k"
      ) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  return createPortal(
    <AnimatePresence>
      {open && <Palette onClose={() => onOpenChange(false)} />}
    </AnimatePresence>,
    document.body,
  );
}

function Palette({ onClose }: { onClose: () => void }) {
  const commands = useCommands();
  const ref = useOverlay<HTMLDivElement>(true, onClose);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const scored = commands
      .map((c) => ({
        c,
        s: Math.max(
          score(q, c.label),
          score(q, `${c.keywords ?? ""} ${c.group}`) * 0.6,
        ),
      }))
      .filter((x) => x.s > 0);
    if (q.trim()) scored.sort((a, b) => b.s - a.s);
    return scored.map((x) => x.c);
  }, [commands, q]);

  const groups = useMemo(() => {
    const m = new Map<string, Command[]>();
    for (const c of results) m.set(c.group, [...(m.get(c.group) ?? []), c]);
    return Array.from(m.entries());
  }, [results]);

  const clamped = Math.min(active, Math.max(0, results.length - 1));
  const run = (c: Command | undefined) => {
    if (!c) return;
    onClose();
    // After the palette's focus restore, so a command that focuses something keeps it.
    setTimeout(c.run, 0);
  };

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${clamped}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [clamped]);

  let index = -1;
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-3 pt-[12vh]">
      <motion.div
        className="absolute inset-0 bg-scrim backdrop-blur-[2px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        aria-hidden="true"
      />
      <motion.div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="relative w-full max-w-[600px] overflow-hidden rounded-[18px] border border-line bg-raised shadow-[var(--shadow-raised)]"
        initial={{ opacity: 0, y: -12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{
          opacity: 0,
          y: -8,
          scale: 0.98,
          transition: { duration: 0.12 },
        }}
        transition={{ type: "spring", stiffness: 520, damping: 36 }}
      >
        <div className="flex items-center gap-3 border-b border-line px-4">
          <span
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-primary-tint text-primary-text"
            aria-hidden="true"
          >
            <Search className="h-4 w-4" />
          </span>
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, results.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                run(results[clamped]);
              }
            }}
            data-autofocus
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={
              results.length ? `${listId}-${clamped}` : undefined
            }
            aria-label="Search pages and actions"
            placeholder="Search pages and actions…"
            className="h-14 flex-1 bg-transparent text-md font-medium text-neutral-900 outline-none placeholder:text-neutral-400"
          />
          <Kbd>Esc</Kbd>
        </div>
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label="Results"
          className="max-h-[min(60vh,420px)] overflow-y-auto p-2"
        >
          {results.length === 0 && (
            <div className="px-3 py-10 text-center text-base text-neutral-500">
              No matches for “{q}”
            </div>
          )}
          {groups.map(([group, cmds]) => (
            <div key={group} role="group" aria-label={group} className="pb-1">
              <div className="px-3 pb-1 pt-2 text-2xs font-bold uppercase tracking-[0.08em] text-neutral-500">
                {group}
              </div>
              {cmds.map((c) => {
                index += 1;
                const i = index;
                const selected = i === clamped;
                return (
                  <div
                    key={c.id}
                    id={`${listId}-${i}`}
                    data-index={i}
                    role="option"
                    aria-selected={selected}
                    tabIndex={-1}
                    onMouseMove={() => setActive(i)}
                    onClick={() => run(c)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") run(c);
                    }}
                    className={cn(
                      "relative flex h-11 cursor-pointer items-center gap-3 rounded-[10px] px-2 text-base font-semibold transition-colors duration-100 [&_svg]:h-4 [&_svg]:w-4",
                      selected
                        ? "bg-primary-tint text-primary-text ring-1 ring-inset ring-primary/15"
                        : "text-neutral-700",
                    )}
                  >
                    <span
                      className={cn(
                        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] border transition-colors",
                        selected
                          ? "border-transparent bg-primary text-on-primary"
                          : "border-line bg-neutral-50 text-neutral-500",
                      )}
                    >
                      {c.icon}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{c.label}</span>
                    {c.shortcut && <Kbd>{c.shortcut}</Kbd>}
                    {selected && (
                      <CornerDownLeft
                        className="!h-4 !w-4 text-primary-text"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 border-t border-line bg-neutral-50 px-4 py-2.5 text-xs font-medium text-neutral-500">
          <span className="inline-flex items-center gap-1.5">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> to move
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Kbd>↵</Kbd> to run
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 font-semibold text-neutral-600">
            <span
              className="h-1.5 w-1.5 rounded-full bg-primary"
              aria-hidden="true"
            />
            BaraPOS
          </span>
        </div>
      </motion.div>
    </div>
  );
}
